import { FunctionComponent, useCallback, useRef } from "react";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import { EMPLOYEE_RANGES } from "@/pages/Private/pages/CompanyEnrich/constants/employees.constants";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { Industry } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import {
	useGetGeoCountriesMutation,
	useGetIndustriesQuery,
	useLazyGetPositionsQuery,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { PersonSearchDefaults } from "@/pages/Private/redux/searchDefaults/searchDefaults.schema";

type ListField = "positionQuery" | "seniority" | "department" | "countries" | "companyCountries";
type CountryField = "countries" | "companyCountries";

const WRITE = { shouldDirty: true, shouldValidate: true };

interface SearchDefaultsFieldsProps {
	values: PersonSearchDefaults;
	setValue: UseFormSetValue<PersonSearchDefaults>;
	/** Names for the codes the stored preset uses, from the API. */
	countryNames: Record<string, string>;
}

const withTitles = <T extends { id: string; title: string }>(ids: string[], options: T[]) =>
	ids.map((id) => ({ id, title: options.find((option) => option.id === id)?.title ?? id }));

/** The editable fields of the company search preset (PREC-3666). */
export const SearchDefaultsFields: FunctionComponent<SearchDefaultsFieldsProps> = ({
	values,
	setValue,
	countryNames,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.searchDefaults.settings.${key}`);

	const { data: industries = [] } = useGetIndustriesQuery();
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const [triggerGetPositions] = useLazyGetPositionsQuery();
	// Names of the countries picked in this session; the stored ones come with the preset.
	const pickedCountryNamesRef = useRef<Map<string, string>>(new Map());

	const setList = (field: ListField, value: string[]) => setValue(field, value, WRITE);

	const setCompanyFilter = (companyFilterSearch: PersonSearchDefaults["companyFilterSearch"]) =>
		setValue("companyFilterSearch", companyFilterSearch, WRITE);

	const fetchPositions = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await triggerGetPositions({ query }).unwrap();

			return result.map((title) => ({ id: title, title }));
		},
		[triggerGetPositions]
	);

	const fetchCountries = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((country) => ({ id: country.code, title: country.name }));
		},
		[getGeoCountries]
	);

	const countryItems = (codes: string[]) =>
		codes.map((code) => ({
			id: code,
			title: pickedCountryNamesRef.current.get(code) ?? countryNames[code] ?? code,
		}));

	const selectCountries = (field: CountryField, items?: CompanyEnrichAutoCompleteItem[]) => {
		items?.forEach((item) => pickedCountryNamesRef.current.set(item.id, item.title));
		setList(field, items?.map((item) => item.id) ?? []);
	};

	/** Keeps a stored industry that the taxonomy no longer lists, rather than dropping it. */
	const selectIndustries = (names: string[]) =>
		setCompanyFilter({
			...values.companyFilterSearch,
			industries: names
				.map(
					(name) =>
						industries.find((industry) => industry.name === name) ??
						values.companyFilterSearch.industries.find((industry) => industry.name === name)
				)
				.filter((industry): industry is Industry => !!industry),
		});

	return (
		<div className="flex flex-col gap-4">
			<CompanyEnrichAutoComplete
				allowCustomValues
				stayOn
				fetchFn={fetchPositions}
				handleSelect={(items) => setList("positionQuery", items?.map((item) => item.id) ?? [])}
				label={ts("jobTitlesLabel")}
				placeholder={ts("jobTitlesPlaceholder")}
				selectedItems={values.positionQuery.map((title) => ({ id: title, title }))}
				testId="search-defaults-job-titles"
			/>
			<div className="grid grid-cols-1 gap-4 tb:grid-cols-2">
				<MultiAutoComplete
					stayOn
					data={SENIORITY_OPTIONS}
					handleSelect={(items) =>
						setList("seniority", items?.map((item) => String(item.id)) ?? [])
					}
					label={ts("seniorityLabel")}
					placeholder={ts("seniorityPlaceholder")}
					selectedItems={withTitles(values.seniority, SENIORITY_OPTIONS)}
				/>
				<MultiAutoComplete
					stayOn
					data={DEPARTMENT_OPTIONS}
					handleSelect={(items) =>
						setList("department", items?.map((item) => String(item.id)) ?? [])
					}
					label={ts("departmentLabel")}
					placeholder={ts("departmentPlaceholder")}
					selectedItems={withTitles(values.department, DEPARTMENT_OPTIONS)}
				/>
			</div>
			<div className="grid grid-cols-1 gap-4 tb:grid-cols-2">
				<CompanyEnrichAutoComplete
					stayOn
					fetchFn={fetchCountries}
					handleSelect={(items) => selectCountries("countries", items)}
					label={ts("personLocationLabel")}
					placeholder={ts("countriesPlaceholder")}
					selectedItems={countryItems(values.countries)}
					testId="search-defaults-person-location"
				/>
				<CompanyEnrichAutoComplete
					stayOn
					fetchFn={fetchCountries}
					handleSelect={(items) => selectCountries("companyCountries", items)}
					label={ts("companyLocationLabel")}
					placeholder={ts("countriesPlaceholder")}
					selectedItems={countryItems(values.companyCountries)}
					testId="search-defaults-company-location"
				/>
			</div>
			<div className="grid grid-cols-1 gap-4 tb:grid-cols-2">
				<MultiAutoComplete
					stayOn
					data={EMPLOYEE_RANGES}
					handleSelect={(items) =>
						setCompanyFilter({
							...values.companyFilterSearch,
							employees: items?.map((item) => String(item.id)) ?? [],
						})
					}
					label={ts("employeesLabel")}
					placeholder={ts("employeesPlaceholder")}
					selectedItems={withTitles(values.companyFilterSearch.employees, EMPLOYEE_RANGES)}
				/>
				<MultiAutoComplete
					stayOn
					data={industries.map((industry) => ({ id: industry.name, title: industry.name }))}
					handleSelect={(items) => selectIndustries(items?.map((item) => String(item.id)) ?? [])}
					label={ts("industriesLabel")}
					placeholder={ts("industriesPlaceholder")}
					selectedItems={values.companyFilterSearch.industries.map((industry) => ({
						id: industry.name,
						title: industry.name,
					}))}
				/>
			</div>
		</div>
	);
};
